import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Tabs,
  Tab,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Avatar,
  Badge
} from '@mui/material';
import DriversManagement from './DriversManagement';
import VehiclesManagement from './VehiclesManagement';
import DocumentManagement from './DocumentManagement';
import RemolquesManagement from './RemolquesManagement';
import TarifariosManagement from './TarifariosManagement';
import CondicionesNegociadasManagement from './CondicionesNegociadasManagement';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { toast } from 'react-toastify';
import { getDriversByTransportLine, getVehiclesByTransportLine } from '../../api';
import TransportLineForm from './TransportLineForm';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TransportLineManagement = ({ selectedLine, onSelectLine, transportLines, onUpdate }) => {
  const [tabValue, setTabValue] = useState(0);
  const [lineDetails, setLineDetails] = useState(null);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [driversWithIncompleteData, setDriversWithIncompleteData] = useState([]);
  const [vehiclesWithIncompleteData, setVehiclesWithIncompleteData] = useState([]);
  const [remolquesWithIncompleteData, setRemolquesWithIncompleteData] = useState([]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (selectedLine) {
      setLineDetails(selectedLine);
      checkForIncompleteData(selectedLine);
    } else {
      setLineDetails(null);
      resetIncompleteData();
    }
  }, [selectedLine]);

  const resetIncompleteData = () => {
    setDriversWithIncompleteData([]);
    setVehiclesWithIncompleteData([]);
    setRemolquesWithIncompleteData([]);
  };

  const checkForIncompleteData = async (line) => {
    if (!line || !line.id) return;

    try {
      const drivers = await getDriversByTransportLine(line.id);
      const incompleteDrivers = drivers.filter(driver => {
        return !driver.nombre || 
               !driver.apellido_paterno || 
               !driver.RFC || 
               !driver.licencia || 
               !driver.telefono;
      });
      setDriversWithIncompleteData(incompleteDrivers);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }

    try {
      const vehicles = await getVehiclesByTransportLine(line.id);
      const incompleteVehicles = vehicles.filter(vehicle => {
        return !vehicle.placas || !vehicle.tipo || !vehicle.modelo || !vehicle.año || !vehicle.color || !vehicle.permisoSCT;
      });
      setVehiclesWithIncompleteData(incompleteVehicles);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }

    try {
      setRemolquesWithIncompleteData([]);
    } catch (error) {
      console.error('Error fetching remolques:', error);
    }
  };

  const handleOpenEditForm = () => {
    setOpenEditForm(true);
  };

  const handleCloseEditForm = () => {
    setOpenEditForm(false);
  };

  const handleEditSubmit = (updatedLine) => {
    setLineDetails(updatedLine);
    onSelectLine(updatedLine);
    onUpdate && onUpdate();
    checkForIncompleteData(updatedLine);
  };

  const sortedTransportLines = transportLines.length > 0 
    ? [...transportLines].sort((a, b) => 
        a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }))
    : [];

  if (!selectedLine && transportLines.length > 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Por favor selecciona una línea de transporte para administrar
        </Alert>
        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <InputLabel>Seleccionar Línea de Transporte</InputLabel>
          <Select
            value=""
            onChange={(e) => {
              const selected = transportLines.find(line => line.id === e.target.value);
              onSelectLine(selected);
            }}
            label="Seleccionar Línea de Transporte"
          >
            {sortedTransportLines.map((line) => (
              <MenuItem key={line.id} value={line.id}>
                {line.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  if (!lineDetails) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          No hay líneas de transporte disponibles
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => {
            toast.info("Función para crear nueva línea");
          }}
        >
          Crear línea de transporte
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <TransportLineForm
        open={openEditForm}
        onClose={handleCloseEditForm}
        onSubmit={handleEditSubmit}
        initialData={lineDetails}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" gutterBottom>
            Líneas de Transporte
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {selectedLine && (
            <Button
              variant="outlined"
              color="primary"
              onClick={() => onSelectLine(null)}
              sx={{ mr: 1 }}
            >
              Limpiar Selección
            </Button>
          )}
        </Grid>
      </Grid>

      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderTop: '5px solid',
          borderColor: lineDetails.blocked ? 'error.main' : (lineDetails.active ? 'success.main' : 'warning.main')
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Badge 
              badgeContent={(driversWithIncompleteData.length + vehiclesWithIncompleteData.length + remolquesWithIncompleteData.length) || null} 
              color="error"
            >
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                <DirectionsBusIcon fontSize="large" />
              </Avatar>
            </Badge>
          </Grid>
          <Grid item xs>
            <Typography variant="h5" component="h2" gutterBottom>
              {lineDetails.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {lineDetails.razon_Social} | SAP: {lineDetails.alta_SAP}
            </Typography>
            {lineDetails.blocked && (
              <Typography variant="body2" color="error">
                BLOQUEADO: {lineDetails.block_reason || 'Sin razón especificada'}
              </Typography>
            )}
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={handleOpenEditForm}
              disabled={lineDetails.blocked}
            >
              Editar información
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 100,
            }
          }}
        >
          <Tab icon={<InfoIcon />} iconPosition="start" label="Información" />
          <Tab 
            icon={
              <Badge badgeContent={driversWithIncompleteData.length || null} color="error">
                <PersonIcon />
              </Badge>
            } 
            iconPosition="start" 
            label="Conductores" 
          />
          <Tab 
            icon={
              <Badge badgeContent={vehiclesWithIncompleteData.length || null} color="error">
                <DirectionsBusIcon />
              </Badge>
            } 
            iconPosition="start" 
            label="Vehículos" 
          />
          <Tab 
            icon={
              <Badge badgeContent={remolquesWithIncompleteData.length || null} color="error">
                <LocalShippingIcon />
              </Badge>
            } 
            iconPosition="start" 
            label="Remolques" 
          />
          <Tab icon={<DescriptionIcon />} iconPosition="start" label="Documentos" />
          <Tab icon={<AttachMoneyIcon />} iconPosition="start" label="Tarifarios" /> 
          <Tab icon={<ReceiptLongIcon />} iconPosition="start" label="Condiciones" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card variant="outlined">
          <CardHeader 
            title="Información General" 
            action={
              (driversWithIncompleteData.length + vehiclesWithIncompleteData.length + remolquesWithIncompleteData.length) > 0 ? (
                <Alert severity="warning" sx={{ my: 1 }}>
                  Hay información incompleta en esta línea de transporte
                </Alert>
              ) : null
            }
          />
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Nombre</Typography>
                <Typography variant="body1">{lineDetails.name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Razón Social</Typography>
                <Typography variant="body1">{lineDetails.razon_Social}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Alta SAP</Typography>
                <Typography variant="body1">{lineDetails.alta_SAP}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Estado</Typography>
                <Typography variant="body1">
                  {lineDetails.blocked ? 'Bloqueado' : (lineDetails.active ? 'Activo' : 'Inactivo')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Teléfono</Typography>
                <Typography variant="body1">{lineDetails.telefono || 'No especificado'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{lineDetails.email || 'No especificado'}</Typography>
              </Grid>
              
              {driversWithIncompleteData.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Hay {driversWithIncompleteData.length} conductor(es) con datos incompletos
                  </Alert>
                </Grid>
              )}
              
              {vehiclesWithIncompleteData.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Hay {vehiclesWithIncompleteData.length} vehículo(s) con datos incompletos
                  </Alert>
                </Grid>
              )}
              
              {remolquesWithIncompleteData.length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Hay {remolquesWithIncompleteData.length} remolque(s) con datos incompletos
                  </Alert>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <DriversManagement selectedLine={lineDetails} onDataChange={checkForIncompleteData} />
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        <VehiclesManagement selectedLine={lineDetails} onDataChange={checkForIncompleteData} />
      </TabPanel>
      
      <TabPanel value={tabValue} index={3}>
        <RemolquesManagement selectedLine={lineDetails} onDataChange={checkForIncompleteData} />
      </TabPanel>
      
      <TabPanel value={tabValue} index={4}>
        <DocumentManagement selectedLine={lineDetails} />
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <TarifariosManagement selectedLine={lineDetails} />
      </TabPanel>

      <TabPanel value={tabValue} index={6}>
        <CondicionesNegociadasManagement selectedLine={lineDetails} />
      </TabPanel>
    </Box>
  );
};

export default TransportLineManagement;
